import { useState, useRef } from "react";
import { ChevronDown, ChevronUp, Upload, X, Image } from "lucide-react";
import TextElementEditor, { RowStyleEditor } from "./TextElementEditor";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Accordion Section component
function AccordionSection({ title, children, defaultOpen = false, icon = null }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="border border-[#E7E5E4] rounded-lg overflow-hidden mb-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-3 bg-[#FAFAF9] hover:bg-[#F5F5F4] transition-colors"
      >
        <div className="flex items-center gap-2">
          {icon}
          <span className="font-medium text-[#44403C]">{title}</span>
        </div>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>
      {isOpen && (
        <div className="p-3 bg-white border-t border-[#E7E5E4]">
          {children}
        </div>
      )}
    </div>
  );
}

export default function TemplateEditorAccordion({ settings, onSettingsChange, saveStatus, getAuthHeaders, guestName = "" }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  
  // Update a nested setting path
  const updateSetting = (path, value) => {
    const newSettings = JSON.parse(JSON.stringify(settings));
    const parts = path.split('.');
    let current = newSettings;
    
    for (let i = 0; i < parts.length - 1; i++) {
      if (!current[parts[i]]) current[parts[i]] = {};
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = value;
    
    onSettingsChange(newSettings);
  };
  
  // Get a nested setting value
  const getSetting = (path, defaultValue = {}) => {
    const parts = path.split('.');
    let current = settings;
    
    for (const part of parts) {
      if (!current || current[part] === undefined) return defaultValue;
      current = current[part];
    }
    return current;
  };
  
  // Replace placeholders for preview
  const previewText = (text) => {
    if (!text) return "";
    return text
      .replace(/\{\{guest_name\}\}/g, guestName || "[Nome Guest]")
      .replace(/\{\{today_date\}\}/g, new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }));
  };
  
  // Handle logo upload
  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (PNG, JPG, GIF, WebP)');
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const headers = getAuthHeaders ? getAuthHeaders().headers : {};
      
      const response = await axios.post(`${API}/admin/preventivo/upload-logo`, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.logo_url) {
        // Force update the settings with the new logo URL
        const newSettings = JSON.parse(JSON.stringify(settings));
        if (!newSettings.footer) newSettings.footer = {};
        if (!newSettings.footer.logo) newSettings.footer.logo = {};
        newSettings.footer.logo.url = response.data.logo_url;
        newSettings.footer.logo.visible = true;
        onSettingsChange(newSettings);
        toast.success('Logo uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Remove logo
  const handleRemoveLogo = async () => {
    try {
      const headers = getAuthHeaders ? getAuthHeaders().headers : {};
      await axios.delete(`${API}/admin/preventivo/remove-logo`, { headers });
      
      // Force update the settings to remove the logo
      const newSettings = JSON.parse(JSON.stringify(settings));
      if (!newSettings.footer) newSettings.footer = {};
      if (!newSettings.footer.logo) newSettings.footer.logo = {};
      newSettings.footer.logo.url = '';
      newSettings.footer.logo.visible = false;
      onSettingsChange(newSettings);
      toast.success('Logo removed');
    } catch (error) {
      console.error('Error removing logo:', error);
      toast.error('Failed to remove logo');
    }
  };
  
  const currentLogoUrl = getSetting('footer.logo.url', '');
  
  return (
    <div className="space-y-2">
      {/* Save status indicator */}
      {saveStatus && (
        <div className={`text-xs px-2 py-1 rounded ${
          saveStatus === 'saving' ? 'bg-yellow-100 text-yellow-700' :
          saveStatus === 'saved' ? 'bg-green-100 text-green-700' :
          saveStatus === 'error' ? 'bg-red-100 text-red-700' : ''
        }`}>
          {saveStatus === 'saving' ? 'Saving...' :
           saveStatus === 'saved' ? 'All changes saved' :
           saveStatus === 'error' ? 'Error saving (will retry)' : ''}
        </div>
      )}
      
      {/* Font Family */}
      <div className="flex items-center gap-2 p-2 bg-[#F9F8F6] rounded-lg mb-3">
        <label className="text-sm">Default Font:</label>
        <select
          value={getSetting('layout.defaultFontFamily', 'times')}
          onChange={(e) => updateSetting('layout.defaultFontFamily', e.target.value)}
          className="flex-1 h-8 px-2 text-sm border border-[#E7E5E4] rounded bg-white"
        >
          <option value="times">Garamond / Times (Serif)</option>
          <option value="helvetica">Helvetica (Sans-serif)</option>
          <option value="courier">Courier (Monospace)</option>
        </select>
      </div>
      
      {/* Header Top Right - "Prepared for" Section */}
      <AccordionSection title="Header Top Right (Prepared for)" defaultOpen={false}>
        <div className="space-y-3">
          <TextElementEditor
            label="Testo 'Prepared for'"
            value={getSetting('headerTopRight.preparedLine', {})}
            onChange={(v) => updateSetting('headerTopRight.preparedLine', v)}
            textPlaceholder="Prepared for Mr. {{guest_name}}"
          />
          
          <TextElementEditor
            label="Linea Luogo/Data"
            value={getSetting('headerTopRight.locationDateLine', {})}
            onChange={(v) => updateSetting('headerTopRight.locationDateLine', v)}
            textPlaceholder="Rome {{today_date}}"
          />
          
          <div className="p-2 bg-[#F9F8F6] rounded text-xs text-[#78716C]">
            <strong>Placeholder disponibili:</strong>
            <br />• <code>{"{{guest_name}}"}</code> = nome del guest
            <br />• <code>{"{{today_date}}"}</code> = data odierna
          </div>
        </div>
      </AccordionSection>
      
      {/* Brand Header */}
      <AccordionSection title="Brand Header" defaultOpen={false}>
        <p className="text-xs text-[#78716C] mb-2">Appears below Header Top Right (centered)</p>
        <TextElementEditor
          label="Brand Title"
          value={getSetting('brandHeader.title', {})}
          onChange={(v) => updateSetting('brandHeader.title', v)}
          textPlaceholder="Suite Lucina"
        />
        <TextElementEditor
          label="Brand Subtitle"
          value={getSetting('brandHeader.subtitle', {})}
          onChange={(v) => updateSetting('brandHeader.subtitle', v)}
          textPlaceholder="Address line"
        />
      </AccordionSection>
      
      {/* Document Title */}
      <AccordionSection title="Document Title" defaultOpen={false}>
        <TextElementEditor
          label="Title"
          value={getSetting('documentTitle', {})}
          onChange={(v) => updateSetting('documentTitle', v)}
          textPlaceholder="Accommodation Proposal"
          showVisibility={true}
        />
      </AccordionSection>
      
      {/* Intro Section */}
      <AccordionSection title="Introduction" defaultOpen={false}>
        <TextElementEditor
          label="Offer Valid Line"
          value={getSetting('intro.offerValid', {})}
          onChange={(v) => updateSetting('intro.offerValid', v)}
          textPlaceholder="Offer valid 24 h"
          showVisibility={true}
        />
        <TextElementEditor
          label="Body Text"
          value={getSetting('intro.bodyText', {})}
          onChange={(v) => updateSetting('intro.bodyText', v)}
          textPlaceholder="Welcome message..."
          showVisibility={true}
        />
      </AccordionSection>
      
      {/* Stay Details */}
      <AccordionSection title="Stay Details" defaultOpen={false}>
        <div className="space-y-3">
          {/* Section alignment control */}
          <div className="p-2 bg-[#F9F8F6] rounded">
            <label className="text-sm text-[#44403C] font-medium mb-2 block">Allineamento Sezione</label>
            <div className="flex gap-2">
              {['left', 'center', 'right'].map(align => (
                <button
                  key={align}
                  type="button"
                  onClick={() => updateSetting('stayDetails.alignment', align)}
                  className={`px-3 py-1 text-xs rounded ${
                    getSetting('stayDetails.alignment', 'left') === align 
                      ? 'bg-[#D4AF37] text-white' 
                      : 'bg-white border border-[#E7E5E4] text-[#44403C] hover:bg-[#F5F5F4]'
                  }`}
                >
                  {align === 'left' ? 'Sinistra' : align === 'center' ? 'Centro' : 'Destra'}
                </button>
              ))}
            </div>
          </div>
          
          <TextElementEditor
            label="Section Title"
            value={getSetting('stayDetails.sectionTitle', {})}
            onChange={(v) => updateSetting('stayDetails.sectionTitle', v)}
            textPlaceholder="Stay Details"
          />
          <TextElementEditor
            label="Check-in/out Line"
            value={getSetting('stayDetails.checkInOutLine', {})}
            onChange={(v) => updateSetting('stayDetails.checkInOutLine', v)}
            showTextInput={false}
            showAlignment={false}
          />
          <TextElementEditor
            label="Guests Line"
            value={getSetting('stayDetails.guestsLine', {})}
            onChange={(v) => updateSetting('stayDetails.guestsLine', v)}
            showTextInput={false}
            showAlignment={false}
          />
          <TextElementEditor
            label="Nightly Rows"
            value={getSetting('stayDetails.nightlyRows', {})}
            onChange={(v) => updateSetting('stayDetails.nightlyRows', v)}
            showTextInput={false}
            showAlignment={false}
          />
          <TextElementEditor
            label="Cleaning Fee"
            value={getSetting('stayDetails.cleaningFee', {})}
            onChange={(v) => updateSetting('stayDetails.cleaningFee', v)}
            showTextInput={false}
            showAlignment={false}
          />
          <TextElementEditor
            label="City Tax"
            value={getSetting('stayDetails.cityTax', {})}
            onChange={(v) => updateSetting('stayDetails.cityTax', v)}
            showTextInput={false}
            showAlignment={false}
          />
        </div>
      </AccordionSection>
      
      {/* Price Breakdown */}
      <AccordionSection title="Price Breakdown" defaultOpen={false}>
        <TextElementEditor
          label="Section Title"
          value={getSetting('priceBreakdown.sectionTitle', {})}
          onChange={(v) => updateSetting('priceBreakdown.sectionTitle', v)}
          textPlaceholder="Price Breakdown"
        />
        <RowStyleEditor
          label="Price Rows Style"
          value={getSetting('priceBreakdown.rows', {})}
          onChange={(v) => updateSetting('priceBreakdown.rows', v)}
        />
        <RowStyleEditor
          label="Subtotal Style"
          value={getSetting('priceBreakdown.subtotal', {})}
          onChange={(v) => updateSetting('priceBreakdown.subtotal', v)}
        />
        <RowStyleEditor
          label="TOTAL Style"
          value={getSetting('priceBreakdown.total', {})}
          onChange={(v) => updateSetting('priceBreakdown.total', v)}
        />
        <div className="flex items-center gap-2 mt-2">
          <label className="text-sm">Divider Color:</label>
          <input
            type="color"
            value={getSetting('priceBreakdown.dividerColor', '#D4AF37')}
            onChange={(e) => updateSetting('priceBreakdown.dividerColor', e.target.value)}
            className="w-10 h-8 border border-[#E7E5E4] rounded cursor-pointer"
          />
        </div>
      </AccordionSection>
      
      {/* Final Notes - After Price Breakdown, Before Footer */}
      <AccordionSection title="Final Notes" defaultOpen={false}>
        <p className="text-xs text-[#78716C] mb-3">
          This section appears after the TOTAL in the PDF. Use it for cancellation terms, legal notes, or additional information.
          <br />
          <strong>Tip:</strong> Leave empty to hide this section from the PDF.
        </p>
        <TextElementEditor
          label="Title"
          value={getSetting('finalNotes.title', {})}
          onChange={(v) => updateSetting('finalNotes.title', v)}
          textPlaceholder="Cancellation Terms"
          showVisibility={true}
        />
        <TextElementEditor
          label="Body Text"
          value={getSetting('finalNotes.bodyText', {})}
          onChange={(v) => updateSetting('finalNotes.bodyText', v)}
          textPlaceholder="Enter cancellation policy, legal notes, or additional terms..."
          showVisibility={true}
          multiline={true}
        />
      </AccordionSection>
      
      {/* Footer - Logo Only (no decorative ornament) */}
      <AccordionSection title="Footer (Logo)" defaultOpen={false}>
        <div className="space-y-4">
          <p className="text-xs text-[#78716C]">
            Upload a logo to display at the bottom of every page in the PDF.
            The logo will be centered and text will not overlap it.
          </p>
          
          {/* Logo Preview */}
          {currentLogoUrl && (
            <div className="relative p-3 bg-[#F9F8F6] rounded-lg">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 flex items-center justify-center bg-white border border-[#E7E5E4] rounded">
                  <img 
                    src={currentLogoUrl} 
                    alt="Logo preview" 
                    className="max-w-full max-h-full object-contain"
                    onError={(e) => {
                      e.target.parentElement.innerHTML = '<span class="text-xs text-red-500">Error</span>';
                    }}
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-[#44403C]">Current Logo</p>
                  <p className="text-xs text-[#78716C]">Will appear on all PDF pages</p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveLogo}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <X size={16} className="mr-1" />
                  Remove
                </Button>
              </div>
            </div>
          )}
          
          {/* Upload Button */}
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
              onChange={handleLogoUpload}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2"
            >
              {uploading ? (
                <>Uploading...</>
              ) : (
                <>
                  <Upload size={16} />
                  {currentLogoUrl ? 'Change Logo' : 'Upload Logo'}
                </>
              )}
            </Button>
            <span className="text-xs text-[#78716C]">PNG, JPG, GIF, WebP (max 2MB)</span>
          </div>
          
          {/* Logo Size Controls */}
          {currentLogoUrl && (
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className="text-xs text-[#78716C] mb-1 block">Width (mm)</label>
                <Input
                  type="number"
                  min="20"
                  max="150"
                  value={getSetting('footer.logo.width', 60)}
                  onChange={(e) => updateSetting('footer.logo.width', parseInt(e.target.value) || 60)}
                  className="text-sm"
                />
              </div>
              <div>
                <label className="text-xs text-[#78716C] mb-1 block">Height (mm)</label>
                <Input
                  type="number"
                  min="10"
                  max="80"
                  value={getSetting('footer.logo.height', 30)}
                  onChange={(e) => updateSetting('footer.logo.height', parseInt(e.target.value) || 30)}
                  className="text-sm"
                />
              </div>
            </div>
          )}
          
          {/* Visibility Toggle */}
          {currentLogoUrl && (
            <label className="flex items-center gap-2 text-sm mt-2">
              <input
                type="checkbox"
                checked={getSetting('footer.logo.visible', true)}
                onChange={(e) => updateSetting('footer.logo.visible', e.target.checked)}
                className="rounded border-[#E7E5E4]"
              />
              Show logo in PDF
            </label>
          )}
        </div>
      </AccordionSection>
    </div>
  );
}
